import React, { useState, useEffect } from 'react'
import { Arrow, Card } from '@ss/meta-core-components'
import { StatisticsIcon } from '../../assets/icons/statisticsIcon'
import styled from '@emotion/styled'
import { Bullet } from '@components'
import { Datum } from '@nivo/bullet'
import { InfoIcon } from '../../assets/icons/infoIcon'
import { normalizeNumber } from '@utils'
import { Axis } from './Axis'
import tooltip from '../../assets/img/tooltipRPE.png'

interface Props {
  data?: Datum[]
}

/**
 * Componente contenente il grafico RPe della pagina time series
 * @param data {(Datum[])=} parametri per la richiesta dei dati
 * @component
 */
export const RPE: React.FC<Props> = ({ data }) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const [normalizedData, setNormalizedData] = useState<Datum[]>([])
  const [minimumRange, setMinimumRange] = useState<number>(0)
  const [normalizedRanges, setNormalizedRanges] = useState<[number, number]>([
    0, 0,
  ])

  useEffect(() => {
    const normalized = (data && data?.map((d) => d.ranges)) || []
    const flattened = normalized?.flat(1)
    const minRange = Math.min(...flattened)
    setMinimumRange(minRange > 0 ? 0 : minRange)
  }, [data])

  useEffect(() => {
    if (!data) return

    const normalized = data?.map((d) => {
      return {
        ...d,
        markers:
          (d.markers &&
            d.markers.map((r) => normalizeNumber(r, minimumRange))) ||
          [],
        measures: d.measures.map((r) => normalizeNumber(r, minimumRange)),
        ranges: d.ranges.map((r) => normalizeNumber(r, minimumRange)),
      }
    })

    const allRanges = (normalized && normalized.map((d) => d.ranges)) || []
    const flattened = allRanges?.flat(1)
    const minNormalizedRange = Math.min(...flattened)
    const maxNormalizedRange = Math.max(...flattened)

    setNormalizedData(normalized || [])
    setNormalizedRanges([minNormalizedRange, maxNormalizedRange])
  }, [minimumRange])

  return (
    <Container data-cy={'RPE-container'}>
      <Card height={'500px'}>
        <Title>
          <div style={{ placeSelf: 'center' }}>
            <StatisticsIcon />
          </div>
          <div>RPE (Revenue per Employee)</div>
          <InfoButton
            data-cy={'RPE-info'}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <InfoIcon />
          </InfoButton>
        </Title>
        <div style={{ display: 'grid', height: '90%' }}>
          <ChartsContainer>
            {normalizedData && Array.isArray(normalizedData) ? (
              normalizedData.map((d, index) => (
                <Bullet
                  dataCy={`RPE-${index}`}
                  dimensions={{ height: '55px' }}
                  key={index}
                  data={[d]}
                  minimumRange={minimumRange}
                  normalizedRanges={normalizedRanges}
                />
              ))
            ) : (
              <></>
            )}
            {normalizedData.length > 0 ? (
              <Axis
                minimumRange={minimumRange}
                normalizedRanges={normalizedRanges}
              />
            ) : (
              <></>
            )}
          </ChartsContainer>
        </div>
      </Card>
      {showTooltip && (
        <TooltipContainer>
          <TooltipContent data-cy={'tooltip-content'}>
            <img alt={'RPE tooltip'} src={tooltip} />
            <TooltipArrowContainer>
              <Arrow direction={'bottom'} />
            </TooltipArrowContainer>
          </TooltipContent>
        </TooltipContainer>
      )}
    </Container>
  )
}

//region STYLE
const Container = styled.div`
  position: relative;
  width: 35%;
`
const Title = styled.div`
  width: 100%;
  font-weight: 500;
  font-size: 18.022px;
  line-height: 22px;
  padding-bottom: 10px;
  display: grid;
  grid-template-columns: 45px auto 130px;
  gap: 30px;
  position: relative;
`
const ChartsContainer = styled.div`
  place-self: center;
  width: 90%;
`
const InfoButton = styled.div`
  place-self: center;
  cursor: pointer;
  border-radius: 20px;
  background-color: var(--secondary);
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  transition: 0.2s all;
  justify-content: center;
  padding: 6px;

  &:hover {
    box-shadow: inset -6px -6px 6px #1e3143, inset 4px 4px 6px #000000;
  }
`
const TooltipContainer = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  flex-direction: column;
  top: -415px;
  left: 0;
  right: 0;
  width: max-content;
  height: 400px;
`
const TooltipContent = styled.div`
  box-shadow: -6px -6px 17px #273447, 6px 6px 17px #000000;
  padding: 10px;
  border-radius: 8px;
  position: relative;
  background: var(--card-background);
  z-index: 9999;
  width: 100%;
  height: 100%;
`
const TooltipArrowContainer = styled.div`
  position: absolute;
  margin: auto;
  height: fit-content;
  width: fit-content;
  display: grid;
  bottom: -9px;
  right: 80px;
`
//endregion
